import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type { BehaviorSignalBus } from '../contracts/signals';
import { BehaviorTrackerBase } from './BehaviorTrackerBase';

interface VisibilityState {
  isVisible: boolean;
  visibleSince: number;
  hiddenAt: number | null;
}

export type VisibilityTrackerOptions = {
  readonly signalBus?: BehaviorSignalBus;
};

export class VisibilityTracker extends BehaviorTrackerBase {
  private state: VisibilityState = {
    isVisible: true,
    visibleSince: 0,
    hiddenAt: null,
  };

  private readonly signalBus: BehaviorSignalBus | undefined;

  constructor(
    context: PixelRuntimeContext,
    options?: VisibilityTrackerOptions
  ) {
    super(context);
    this.signalBus = options?.signalBus;
  }

  protected onInit(): void {
    if (!this.isDocumentAvailable()) return;

    const now = Date.now();
    const isVisible = document.visibilityState === 'visible';

    this.state = {
      isVisible,
      visibleSince: now,
      hiddenAt: isVisible ? null : now,
    };

    this.publishVisibilitySignal();

    this.addDocumentListener('visibilitychange', () =>
      this.handleVisibilityChange()
    );
    this.emitVisibilityEvent();
  }

  protected onDestroy(): void {}

  private handleVisibilityChange(): void {
    if (!this.isDocumentAvailable()) return;

    const now = Date.now();
    const isVisible = document.visibilityState === 'visible';

    if (isVisible) {
      this.state = {
        isVisible: true,
        visibleSince: now,
        hiddenAt: null,
      };
    } else {
      this.state = {
        isVisible: false,
        visibleSince: this.state.visibleSince,
        hiddenAt: now,
      };
    }

    this.publishVisibilitySignal();
    this.emitVisibilityEvent();
  }

  private publishVisibilitySignal(): void {
    if (!this.signalBus) return;
    if (this.state.isVisible) {
      this.signalBus.publish(
        this.signalBus.createSignal('VISIBLE', 'VisibilityTracker', {
          visibleSince: this.state.visibleSince,
        })
      );
    } else {
      this.signalBus.publish(
        this.signalBus.createSignal('HIDDEN', 'VisibilityTracker', {
          hiddenAt: this.state.hiddenAt,
        })
      );
    }
  }

  private emitVisibilityEvent(): void {
    this.dispatchEngagement({
      type: 'VISIBILITY_CHANGE',
      category: 'ENGAGEMENT',
      isVisible: this.state.isVisible,
      visibleSince: this.state.visibleSince,
      hiddenAt: this.state.hiddenAt,
    });
  }
}
